import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { WritableSelect } from '../components/ui/writable-select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import type { Sale, CarBrand, CarModel, Seller } from '../types/car';
import { api } from '../lib/api';

interface EditSaleFormData {
  salePrice: string | number;
  deposit: string | number;
  currency: string;
  buyerId?: number;
  buyerName?: string;
  buyerIdNo?: string;
  buyerAddress?: string;
  buyerContact?: string;
  sellerName?: string;
  sellerIdNo?: string;
  sellerAddress?: string;
  sellerContact?: string;
  notes?: string;
  // Car details
  carYear: number;
  carColor: string;
  carPlateNo: string;
  carChassisNo: string;
  carRegisteredOwner: string;
}

export default function EditCar() {
  const { t } = useLanguage();
  const { carId } = useParams<{ carId: string }>();
  const saleId = carId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sale, setSale] = useState<Sale | null>(null);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeller, setSelectedSeller] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { },
  } = useForm<EditSaleFormData>({
    defaultValues: {
      salePrice: '',
      deposit: '',
      currency: 'USD',
      carYear: new Date().getFullYear(),
      carColor: '',
      carPlateNo: '',
      carChassisNo: '',
      carRegisteredOwner: '',
    },
  });

  const currentBrandId = selectedBrand ? brands.find((b) => b.name === selectedBrand)?.id ?? 0 : 0;

  useEffect(() => {
    if (saleId) {
      (async () => {
        setLoading(true);
        try {
          const [saleData, brandsRes, modelsRes, sellersRes] = await Promise.all([
            api.get(`/api/sales/${saleId}`),
            api.get('/api/brands'),
            api.get('/api/models'),
            api.get('/api/sellers'),
          ]);
          setSale(saleData);
          setBrands(Array.isArray(brandsRes) ? brandsRes : []);
          setModels(Array.isArray(modelsRes) ? modelsRes : []);
          const s = (sellersRes as { sellers?: Seller[] })?.sellers ?? sellersRes;
          setSellers(Array.isArray(s) ? s : []);
          if (saleData) {
            setValue('salePrice', saleData.salePrice);
            setValue('deposit', saleData.deposit);
            setValue('currency', (saleData.warehouseCar as any)?.purchaseCurrency ?? saleData.currency ?? 'USD');
            setValue('sellerName', saleData.sellerName || '');
            setValue('sellerIdNo', saleData.sellerIdNo || '');
            setValue('sellerAddress', saleData.sellerAddress || '');
            setValue('sellerContact', saleData.sellerContact || '');
            setSelectedSeller(saleData.sellerName || '');
            setValue('buyerName', saleData.buyer?.name || '');
            setValue('buyerIdNo', saleData.buyer?.idNo || '');
            setValue('buyerAddress', saleData.buyer?.address || '');
            setValue('buyerContact', saleData.buyer?.contact || '');
            setValue('notes', saleData.notes || '');
            if (saleData.buyer) {
              setValue('buyerId', saleData.buyer.id);
            }
            // Car details
            const car = saleData.warehouseCar;
            if (car) {
              setSelectedBrand(car.brand?.name || '');
              setSelectedModel(car.model?.name || '');
              setValue('carYear', car.year);
              setValue('carColor', car.color || '');
              setValue('carPlateNo', car.plateNumber || '');
              setValue('carChassisNo', car.chassisNo || '');
              setValue('carRegisteredOwner', car.registeredOwner || '');
            }
          }
        } catch (e) {
          console.error(e);
          navigate('/sell-car-history');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [saleId, navigate, setValue]);

  const handleBrandChange = (name: string) => {
    setSelectedBrand(name);
    setSelectedModel('');
  };

  const handleModelChange = (name: string) => {
    setSelectedModel(name);
  };

  const createBrand = async (name: string) => {
    const newBrand = await api.post('/api/brands', { name, active: true });
    setBrands((prev) => [...prev, newBrand]);
    setSelectedBrand(newBrand.name);
  };

  const createModel = async (name: string) => {
    const newModel = await api.post('/api/models', { name, brandId: currentBrandId, active: true });
    setModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel.name);
  };

  const createSeller = async (name: string) => {
    const newSeller = await api.post('/api/sellers', {
      name,
      idNo: watch('sellerIdNo') || undefined,
      address: watch('sellerAddress') || undefined,
      contact: watch('sellerContact') || 'N/A',
    });
    setSellers((prev) => [...prev, newSeller]);
    setSelectedSeller(newSeller.name);
    setValue('sellerName', newSeller.name);
    setValue('sellerContact', newSeller.contact);
    setValue('sellerIdNo', newSeller.idNo || '');
    setValue('sellerAddress', newSeller.address || '');
  };

  const handleSellerChange = (name: string) => {
    setSelectedSeller(name);
    const s = sellers.find((x) => x.name === name);
    if (s) {
      setValue('sellerName', s.name);
      setValue('sellerContact', s.contact);
      setValue('sellerIdNo', s.idNo || '');
      setValue('sellerAddress', s.address || '');
    } else {
      setValue('sellerName', '');
      setValue('sellerContact', '');
      setValue('sellerIdNo', '');
      setValue('sellerAddress', '');
    }
  };

  const onSubmit = async (data: EditSaleFormData) => {
    if (!saleId || !sale) return;
    setSubmitting(true);
    try {
      const saleCurrency = (sale.warehouseCar as any)?.purchaseCurrency ?? sale.currency ?? data.currency ?? 'USD';

      // Resolve brand & model IDs
      const brandObj = brands.find((b) => b.name === selectedBrand);
      const modelObj = models.find((m) => m.name === selectedModel);
      const brandId = brandObj?.id;
      const modelId = modelObj?.id;

      // Update the warehouse car details (send null when cleared so backend can set empty)
      await api.put(`/api/warehouse-cars/${sale.warehouseCarId}`, {
        brandId: brandId || undefined,
        modelId: modelId || undefined,
        year: data.carYear,
        color: data.carColor || undefined,
        plateNumber: (data.carPlateNo ?? '').trim() || null,
        chassisNo: (data.carChassisNo ?? '').trim() || null,
        registeredOwner: (data.carRegisteredOwner ?? '').trim() || null,
      });

      // Update the sale (include buyer details so backend can create/update/clear Buyer)
      const buyerNameTrim = (data.buyerName ?? '').trim();
      const buyerContactTrim = (data.buyerContact ?? '').trim();
      await api.put(`/api/sales/${saleId}`, {
        buyerId: (buyerNameTrim || buyerContactTrim) ? (data.buyerId ?? null) : null,
        buyerName: data.buyerName ?? '',
        buyerIdNo: data.buyerIdNo ?? '',
        buyerAddress: data.buyerAddress ?? '',
        buyerContact: data.buyerContact ?? '',
        salePrice: typeof data.salePrice === 'string' ? parseFloat(data.salePrice) : data.salePrice,
        deposit: typeof data.deposit === 'string' ? parseFloat(data.deposit) : data.deposit,
        currency: saleCurrency,
        sellerName: data.sellerName || null,
        sellerIdNo: data.sellerIdNo || null,
        sellerAddress: data.sellerAddress || null,
        sellerContact: data.sellerContact || null,
        notes: data.notes || null,
      });
      toast.success(t('sellCar.updateSuccess'));
      navigate('/sell-car-history');
    } catch (e: any) {
      toast.error(e?.error ?? t('sellCar.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">Loading…</span>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 mb-4">{t('sellCar.carNotFound')}</h1>
          <Button onClick={() => navigate('/sell-car-history')} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('sellCar.backToHistory')}
          </Button>
        </div>
      </div>
    );
  }

  const car = sale.warehouseCar;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/sell-car-history')} className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('carDetails.backToHistory')}
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('sellCar.editTitle')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {car.brand.name} {car.model.name} • {car.year} • Sale #{sale.id}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Car Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.carDetails')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellCar.brand')} *</Label>
                  <WritableSelect
                    value={selectedBrand}
                    onValueChange={handleBrandChange}
                    options={brands}
                    onCreateNew={createBrand}
                    placeholder={t('sellCar.brand')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.model')} *</Label>
                  <WritableSelect
                    value={selectedModel}
                    onValueChange={handleModelChange}
                    options={models.filter((m) => m.brandId === currentBrandId)}
                    onCreateNew={createModel}
                    placeholder={t('sellCar.model')}
                    disabled={!currentBrandId}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.year')} *</Label>
                  <Input type="number" {...register('carYear', { valueAsNumber: true })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.color')}</Label>
                  <Input {...register('carColor')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.plateNumber')}</Label>
                  <Input {...register('carPlateNo')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.chassisNo')}</Label>
                  <Input {...register('carChassisNo')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.registeredOwner')}</Label>
                  <Input {...register('carRegisteredOwner')} placeholder="صاحب سنوية" />
                </div>
              </div>
            </div>

            {/* Seller Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.sellerDetails')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellCar.selectSeller')}</Label>
                  <WritableSelect
                    value={selectedSeller}
                    onValueChange={handleSellerChange}
                    options={sellers}
                    onCreateNew={createSeller}
                    placeholder={t('sellCar.selectSellerPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.sellerName')}</Label>
                  <Input {...register('sellerName')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.sellerContact')}</Label>
                  <Input {...register('sellerContact')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.sellerIdNo')}</Label>
                  <Input {...register('sellerIdNo')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.sellerAddress')}</Label>
                  <Input {...register('sellerAddress')} />
                </div>
              </div>
            </div>

            {/* Buyer Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.buyerDetails')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellCar.buyerName')}</Label>
                  <Input {...register('buyerName')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.buyerContact')}</Label>
                  <Input {...register('buyerContact')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.buyerIdNo')}</Label>
                  <Input {...register('buyerIdNo')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.buyerAddress')}</Label>
                  <Input {...register('buyerAddress')} />
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.paymentDetails')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellCar.salePrice')} *</Label>
                  <Input type="number" step="any" {...register('salePrice')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellCar.deposit')}</Label>
                  <Input type="number" step="any" {...register('deposit')} />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Input
                    value={(watch('currency') || (car as any).purchaseCurrency) ?? sale.currency ?? 'USD'}
                    readOnly
                    className="bg-slate-50 cursor-not-allowed border-slate-200"
                  />
                  <input type="hidden" {...register('currency')} />
                  <p className="text-xs text-slate-500">Matches purchase currency (cannot be changed)</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('sellCar.notes')}</Label>
              <Input {...register('notes')} placeholder={t('sellCar.notesPlaceholder')} />
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 disabled:pointer-events-none">
                {submitting ? t('sellCar.updating') : t('sellCar.updateCar')}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/sell-car-history')} className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg px-4 py-2.5 text-sm font-medium">
                {t('sellCar.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
